import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import debugFactory from 'debug';
import React from 'react';

import { Category, CategoryOption, Choice, StateResponse } from '../../api/api';
import { OptimisticChoiceContext, PreviousChoiceContext } from '../category/category-manager';
import { apiInstance, useGlobalState, useSessionState } from '../global-state';

const debug = debugFactory('ingrid:user_choice');

/*
 * v4 API does not return choices, but it requires a choice to make an update -
 * construct it here.
 *
 * If category option or time slot not provided, use the first from list.
 */
export function constructUserChoice(
  category: Category,
  categoryOption?: CategoryOption,
  timeSlotId?: string
): Choice {
  const referenceOption = categoryOption ?? category.options[0];
  return {
    shipping_category: category.id,
    shipping_method: referenceOption.shipping_method,
    location_ref: referenceOption.location?.id,
    // BE: id should be required in DeliveryTime
    time_slot_ref: timeSlotId ?? referenceOption.delivery_times[0].id!,
  };
}

/*
 * Reconstruct a user choice against current state in case timeslot was
 * regenerated in the background or an option disappeared.
 *
 * If category option or time slot is not provided - use the first
 * from list.
 */
export function reconstructUserChoice(category: Category, userChoice: Choice): Choice {
  const selectedOption =
    category.options.find(option => {
      return (
        option.shipping_method === userChoice.shipping_method &&
        option.location?.id === userChoice.location_ref
      );
    }) || category.options[0];
  // TODO: Is there a need to match option by shipping method if location disappeared ?

  const selectedDeliveryTime =
    selectedOption.delivery_times.find(deliveryTime => {
      return deliveryTime.id === userChoice.time_slot_ref;
    }) || selectedOption.delivery_times[0];

  return {
    shipping_category: userChoice.shipping_category,
    shipping_method: selectedOption.shipping_method,
    location_ref: selectedOption.location?.id,
    // BE: id should be required in DeliveryTime
    time_slot_ref: selectedDeliveryTime.id!,
  };
}

export function compareChoice(a: Choice, b: Choice) {
  return (
    a.shipping_category === b.shipping_category &&
    a.shipping_method === b.shipping_method &&
    a.location_ref === b.location_ref
  );
  // Note: delivery time is usually irrelevant in any matching.
}

export function useUserChoiceMutation() {
  const state = useGlobalState();
  const session = useSessionState();

  const [_, setOptimisticChoice] = React.useContext(OptimisticChoiceContext);
  const [_previousChoice, setPreviousChoice] = React.useContext(PreviousChoiceContext);

  const mutationFn = async (userChoice: Choice) => {
    const { data } = await apiInstance.v4.userChoicePatch(
      state.checkout_session_id,
      session.session_id,
      userChoice
    );
    return data;
  };

  return useMutation<StateResponse, AxiosError, Choice>({
    mutationFn,
    mutationKey: ['user-choice'],
    onMutate: (newChoice: Choice) => {
      setOptimisticChoice(newChoice);
      setPreviousChoice(newChoice);

      debug(`Setup optimistic choice: ${newChoice.shipping_category}`);
    },
  });
}
